import React, { useState, useRef, useEffect } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Space, Table, Divider, Tag, Typography, Button, Modal, Descriptions, Collapse } from 'antd';
import { DollarOutlined, RiseOutlined, FallOutlined, UserOutlined, TeamOutlined, BookOutlined, WalletOutlined, PrinterOutlined, FileTextOutlined, BarChartOutlined, PieChartOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import api from '../utils/api';
import config from '../config';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const Reports = () => {
    const { t } = useTranslation();
    const { user, updateUser } = useAuthStore();
    const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [invoiceVisible, setInvoiceVisible] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceType, setInvoiceType] = useState('income');
    const invoiceRef = useRef();

    // Fetch and update academy data on component mount
    useEffect(() => {
        const fetchAcademyData = async () => {
            if (user) {
                try {
                    const response = await api.get('/academy');
                    if (response.data.data) {
                        // Update user with fresh academy data including logo
                        updateUser({ ...user, academy: response.data.data });
                    }
                } catch (error) {
                    console.error('Failed to fetch academy data:', error);
                }
            }
        };
        fetchAcademyData();
    }, []); // Run only once on mount

    const handlePrint = useReactToPrint({
        content: () => invoiceRef.current,
        onBeforeGetContent: () => {
            return new Promise((resolve) => {
                const images = invoiceRef.current?.getElementsByTagName('img');
                if (!images || images.length === 0) {
                    resolve();
                    return;
                }

                const imagePromises = Array.from(images).map(img => {
                    if (img.complete) return Promise.resolve();
                    return new Promise((res) => {
                        img.onload = res;
                        img.onerror = res;
                        // Timeout after 2 seconds
                        setTimeout(res, 2000);
                    });
                });

                Promise.all(imagePromises).then(resolve);
            });
        },
    });

    // Fetch all data
    const { data: incomeData, isLoading: incomeLoading } = useQuery('income', async () => {
        const response = await api.get('/income', {
            params: { limit: 10000 }
        });
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    const { data: expensesData, isLoading: expensesLoading } = useQuery('expenses', async () => {
        const response = await api.get('/expenses');
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    const { data: studentsData, isLoading: studentsLoading } = useQuery('students', async () => {
        const response = await api.get('/students');
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    const { data: staffData, isLoading: staffLoading } = useQuery('staff', async () => {
        const response = await api.get('/staff');
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    const { data: coursesData, isLoading: coursesLoading } = useQuery('courses', async () => {
        const response = await api.get('/courses');
        return response.data.data;
    }, {
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    // Filter data by date range
    const filterByDateRange = (data) => {
        if (!dateRange || !data || !Array.isArray(data)) return data || [];
        return data.filter(item => {
            const itemDate = dayjs(item.date || item.createdAt);
            return itemDate.isAfter(dateRange[0]) && itemDate.isBefore(dateRange[1]);
        });
    };

    const filteredIncome = filterByDateRange(incomeData);
    const filteredExpenses = filterByDateRange(expensesData);

    // Calculate student enrollments by course (currency-separated)
    const enrollmentsByCourse = studentsData?.reduce((acc, student) => {
        student.enrollments?.forEach(enrollment => {
            if (enrollment.status === 'active' || enrollment.status === 'completed') {
                // Check if course exists
                if (!enrollment.course) return;

                const courseId = enrollment.course._id || enrollment.course;
                const currency = enrollment.payment?.currency || 'USD';

                if (!acc[courseId]) {
                    acc[courseId] = {
                        courseName: enrollment.course.name || 'Unknown Course',
                        courseCode: enrollment.course.code || 'N/A',
                        count: 0,
                        currency: currency,
                        totalRevenue: 0,
                        paidAmount: 0,
                        pendingAmount: 0,
                        students: []
                    };
                }
                acc[courseId].count++;
                acc[courseId].totalRevenue += enrollment.payment?.totalFee || 0;
                acc[courseId].paidAmount += enrollment.payment?.paidAmount || 0;
                acc[courseId].pendingAmount += enrollment.payment?.remainingAmount || 0;
                acc[courseId].students.push({
                    name: `${student.firstName} ${student.lastName}`,
                    status: enrollment.payment?.paymentStatus || 'pending',
                    paid: enrollment.payment?.paidAmount || 0,
                    total: enrollment.payment?.totalFee || 0,
                    currency: currency
                });
            }
        });
        return acc;
    }, {}) || {};

    // Calculate totals
    const totalIncome = filteredIncome?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
    const totalExpenses = filteredExpenses?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;

    // Calculate totals by currency (default to USD if currency is not set)
    const totalIncomeIQD = filteredIncome?.filter(item => item.currency === 'IQD').reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
    const totalIncomeUSD = filteredIncome?.filter(item => !item.currency || item.currency === 'USD').reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
    const totalExpensesIQD = filteredExpenses?.filter(item => item.currency === 'IQD').reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
    const totalExpensesUSD = filteredExpenses?.filter(item => !item.currency || item.currency === 'USD').reduce((sum, item) => sum + (item.amount || 0), 0) || 0;

    // Calculate course revenue for display (from student enrollments) - separated by currency
    const courseRevenueIQD = Object.values(enrollmentsByCourse).reduce((sum, course) => {
        return sum + (course.currency === 'IQD' ? course.paidAmount : 0);
    }, 0);

    const courseRevenueUSD = Object.values(enrollmentsByCourse).reduce((sum, course) => {
        return sum + (course.currency !== 'IQD' ? course.paidAmount : 0);
    }, 0);

    // Net profit calculation
    const netProfit = totalIncome - totalExpenses;
    const netProfitIQD = totalIncomeIQD - totalExpensesIQD;
    const netProfitUSD = totalIncomeUSD - totalExpensesUSD;

    // Calculate income breakdown by currency (default to USD if currency is not set)
    const incomeByCategoryIQD = filteredIncome?.filter(item => item.currency === 'IQD').reduce((acc, item) => {
        acc[item.category] = (acc[item.category] || 0) + item.amount;
        return acc;
    }, {}) || {};

    const incomeByCategoryUSD = filteredIncome?.filter(item => !item.currency || item.currency === 'USD').reduce((acc, item) => {
        acc[item.category] = (acc[item.category] || 0) + item.amount;
        return acc;
    }, {}) || {};

    const tuitionIncomeIQD = incomeByCategoryIQD['course-fee'] || 0;
    const registrationIncomeIQD = incomeByCategoryIQD['registration-fee'] || 0;
    const otherIncomeIQD = (incomeByCategoryIQD['material-fee'] || 0) + (incomeByCategoryIQD['exam-fee'] || 0) + (incomeByCategoryIQD.other || 0);

    const tuitionIncomeUSD = incomeByCategoryUSD['course-fee'] || 0;
    const registrationIncomeUSD = incomeByCategoryUSD['registration-fee'] || 0;
    const otherIncomeUSD = (incomeByCategoryUSD['material-fee'] || 0) + (incomeByCategoryUSD['exam-fee'] || 0) + (incomeByCategoryUSD.other || 0);

    // Calculate expense breakdown by currency (default to USD if currency is not set)
    const expenseByCategoryIQD = filteredExpenses?.filter(item => item.currency === 'IQD').reduce((acc, item) => {
        const category = item.category || 'other';
        acc[category] = (acc[category] || 0) + (item.amount || 0);
        return acc;
    }, {}) || {};

    const expenseByCategoryUSD = filteredExpenses?.filter(item => !item.currency || item.currency === 'USD').reduce((acc, item) => {
        const category = item.category || 'other';
        acc[category] = (acc[category] || 0) + (item.amount || 0);
        return acc;
    }, {}) || {};

    // Get all unique expense categories for dynamic breakdown
    const allExpenseCategoriesIQD = Object.keys(expenseByCategoryIQD);
    const allExpenseCategoriesUSD = Object.keys(expenseByCategoryUSD);

    // Count active students
    const activeStudents = studentsData?.filter(s => s.status === 'active')?.length || 0;
    const activeStaff = staffData?.filter(s => s.status === 'active')?.length || 0;

    // Get students with pending payments
    const unpaidStudents = studentsData?.reduce((acc, student) => {
        student.enrollments?.forEach(enrollment => {
            if (enrollment.status === 'active' && enrollment.payment?.remainingAmount > 0) {
                // Check if course exists
                if (!enrollment.course) return;

                acc.push({
                    key: `${student._id}-${enrollment.course._id || enrollment.course}`,
                    studentId: student.studentId,
                    studentName: `${student.firstName} ${student.lastName}`,
                    courseName: enrollment.course.name || 'Unknown',
                    courseCode: enrollment.course.code || 'N/A',
                    currency: enrollment.payment?.currency || 'USD',
                    totalFee: enrollment.payment?.totalFee || 0,
                    paidAmount: enrollment.payment?.paidAmount || 0,
                    remainingAmount: enrollment.payment?.remainingAmount || 0,
                    paymentStatus: enrollment.payment?.paymentStatus || 'pending',
                    student: student,
                    enrollment: enrollment,
                    phone: student.phone,
                    email: student.email,
                });
            }
        });
        return acc;
    }, []) || [];

    // Get all payment history from all student enrollments
    const allPaymentHistory = studentsData?.reduce((acc, student) => {
        student.enrollments?.forEach(enrollment => {
            if (enrollment.payment?.paymentHistory && enrollment.payment.paymentHistory.length > 0) {
                enrollment.payment.paymentHistory.forEach((payment, index) => {
                    acc.push({
                        key: `${student._id}-${enrollment._id}-${index}`,
                        studentId: student.studentId,
                        studentName: `${student.firstName} ${student.lastName}`,
                        courseName: enrollment.course?.name || 'Unknown',
                        courseCode: enrollment.course?.code || 'N/A',
                        currency: enrollment.payment?.currency || 'USD',
                        amount: payment.amount || 0,
                        discount: payment.discount || 0,
                        netAmount: (payment.amount || 0) - (payment.discount || 0),
                        paymentMethod: payment.paymentMethod || 'cash',
                        paymentDate: payment.paymentDate || payment.date,
                        recordedBy: payment.recordedBy,
                        student: student,
                        enrollment: enrollment
                    });
                });
            }
        });
        return acc;
    }, []) || [];

    // Filter payment history by date range
    const filteredPaymentHistory = filterByDateRange(allPaymentHistory.map(p => ({
        ...p,
        date: p.paymentDate
    })));

    // Calculate total discounts by currency from payment history
    const totalDiscountIQD = filteredPaymentHistory?.filter(p => p.currency === 'IQD').reduce((sum, item) => sum + (item.discount || 0), 0) || 0;
    const totalDiscountUSD = filteredPaymentHistory?.filter(p => (!p.currency || p.currency === 'USD')).reduce((sum, item) => sum + (item.discount || 0), 0) || 0;

    // Show invoice modal
    const showInvoice = (record, type) => {
        setSelectedInvoice(record);
        setInvoiceType(type);
        setInvoiceVisible(true);
    };

    // Show student payment invoice
    const showStudentInvoice = (record) => {
        const invoiceData = {
            student: {
                firstName: record.studentName.split(' ')[0],
                lastName: record.studentName.split(' ').slice(1).join(' '),
            },
            date: record.enrollment?.startDate || new Date(),
            category: 'tuition',
            description: `${record.courseName} - ${t('pendingAmount')}`,
            amount: record.remainingAmount,
            receiptNumber: `INV-${record.studentId}-${dayjs().format('YYYYMMDD')}`,
            paymentMethod: 'pending',
            course: record.courseName,
            totalFee: record.totalFee,
            paidAmount: record.paidAmount,
            remainingAmount: record.remainingAmount,
        };
        setSelectedInvoice(invoiceData);
        setInvoiceType('unpaid');
        setInvoiceVisible(true);
    };

    // Course enrollment stats table
    const courseStatsColumns = [
        { title: t('courseCode'), dataIndex: 'courseCode', key: 'courseCode' },
        { title: t('courseName'), dataIndex: 'courseName', key: 'courseName' },
        {
            title: t('currency'),
            dataIndex: 'currency',
            key: 'currency',
            render: (currency) => <Tag color={currency === 'IQD' ? 'blue' : 'green'}>{currency || 'USD'}</Tag>
        },
        {
            title: t('enrolledStudents'),
            dataIndex: 'count',
            key: 'count',
            render: (count) => <Tag color="blue">{count}</Tag>
        },
        {
            title: t('totalRevenue'),
            dataIndex: 'totalRevenue',
            key: 'totalRevenue',
            render: (amount, record) => {
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text strong>{symbol}{amount.toLocaleString()}</Text>;
            }
        },
        {
            title: t('paidAmount'),
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            render: (amount, record) => {
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text style={{ color: '#3f8600' }}>{symbol}{amount.toLocaleString()}</Text>;
            }
        },
        {
            title: t('pendingAmount'),
            dataIndex: 'pendingAmount',
            key: 'pendingAmount',
            render: (amount, record) => {
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text style={{ color: '#cf1322' }}>{symbol}{amount.toLocaleString()}</Text>;
            }
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<FileTextOutlined />}
                    style={{
                        background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                        border: 'none',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(67, 233, 123, 0.3)'
                    }}
                    onClick={() => {
                        Modal.info({
                            title: t('courseDetails'),
                            width: 700,
                            content: (
                                <div>
                                    <Descriptions bordered size="small" column={2}>
                                        <Descriptions.Item label={t('courseCode')} span={1}>{record.courseCode}</Descriptions.Item>
                                        <Descriptions.Item label={t('courseName')} span={1}>{record.courseName}</Descriptions.Item>
                                        <Descriptions.Item label={t('currency')} span={2}>
                                            <Tag color={record.currency === 'IQD' ? 'blue' : 'green'}>{record.currency}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('enrolledStudents')} span={2}>{record.count}</Descriptions.Item>
                                        <Descriptions.Item label={t('totalRevenue')} span={2}>
                                            {record.currency === 'IQD' ? 'IQD ' : '$'}{record.totalRevenue.toLocaleString()}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('paidAmount')} span={2}>
                                            <Text style={{ color: '#3f8600' }}>{record.currency === 'IQD' ? 'IQD ' : '$'}{record.paidAmount.toLocaleString()}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('pendingAmount')} span={2}>
                                            <Text style={{ color: '#cf1322' }}>{record.currency === 'IQD' ? 'IQD ' : '$'}{record.pendingAmount.toLocaleString()}</Text>
                                        </Descriptions.Item>
                                    </Descriptions>
                                    <Divider>{t('enrolledStudentsList')}</Divider>
                                    <Table
                                        size="small"
                                        columns={[
                                            { title: t('studentName'), dataIndex: 'name', key: 'name' },
                                            {
                                                title: t('paidAmount'),
                                                dataIndex: 'paid',
                                                key: 'paid',
                                                render: (paid) => `${record.currency === 'IQD' ? 'IQD ' : '$'}${paid.toLocaleString()}`
                                            },
                                            {
                                                title: t('totalFee'),
                                                dataIndex: 'total',
                                                key: 'total',
                                                render: (total) => `${record.currency === 'IQD' ? 'IQD ' : '$'}${total.toLocaleString()}`
                                            },
                                            {
                                                title: t('status'),
                                                dataIndex: 'status',
                                                key: 'status',
                                                render: (status) => <Tag color={status === 'completed' ? 'green' : status === 'partial' ? 'orange' : 'red'}>{t(status)}</Tag>
                                            },
                                        ]}
                                        dataSource={record.students}
                                        pagination={false}
                                    />
                                </div>
                            ),
                        });
                    }}
                >
                    {t('view')}
                </Button>
            ),
        },
    ];

    const courseStatsData = Object.values(enrollmentsByCourse);

    // Unpaid students table
    const unpaidStudentsColumns = [
        {
            title: t('studentId'),
            dataIndex: 'studentId',
            key: 'studentId',
        },
        {
            title: t('studentName'),
            dataIndex: 'studentName',
            key: 'studentName',
        },
        {
            title: t('courseCode'),
            dataIndex: 'courseCode',
            key: 'courseCode',
        },
        {
            title: t('courseName'),
            dataIndex: 'courseName',
            key: 'courseName',
        },
        {
            title: t('currency'),
            dataIndex: 'currency',
            key: 'currency',
            render: (currency) => <Tag color={currency === 'IQD' ? 'blue' : 'green'}>{currency || 'USD'}</Tag>
        },
        {
            title: t('totalFee'),
            dataIndex: 'totalFee',
            key: 'totalFee',
            render: (amount, record) => {
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text>{symbol}{amount.toLocaleString()}</Text>;
            }
        },
        {
            title: t('paidAmount'),
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            render: (amount, record) => {
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text style={{ color: '#52c41a' }}>{symbol}{amount.toLocaleString()}</Text>;
            }
        },
        {
            title: t('remainingAmount'),
            dataIndex: 'remainingAmount',
            key: 'remainingAmount',
            render: (amount, record) => {
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text strong style={{ color: '#ff4d4f' }}>{symbol}{amount.toLocaleString()}</Text>;
            }
        },
        {
            title: t('paymentStatus'),
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status) => {
                const colors = { paid: 'green', partial: 'orange', pending: 'red' };
                return <Tag color={colors[status] || 'default'}>{t(status)}</Tag>;
            }
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="default"
                        size="small"
                        icon={<FileTextOutlined />}
                        style={{
                            borderColor: '#667eea',
                            color: '#667eea',
                            borderRadius: '6px'
                        }}
                        onClick={() => {
                            Modal.info({
                                title: t('studentPaymentDetails'),
                                width: 600,
                                content: (
                                    <Descriptions bordered size="small" column={1}>
                                        <Descriptions.Item label={t('studentId')}>{record.studentId}</Descriptions.Item>
                                        <Descriptions.Item label={t('studentName')}>{record.studentName}</Descriptions.Item>
                                        <Descriptions.Item label={t('courseCode')}>{record.courseCode}</Descriptions.Item>
                                        <Descriptions.Item label={t('courseName')}>{record.courseName}</Descriptions.Item>
                                        <Descriptions.Item label={t('currency')}>
                                            <Tag color={record.currency === 'IQD' ? 'blue' : 'green'}>{record.currency || 'USD'}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('totalFee')}>
                                            <Text>{record.currency === 'IQD' ? 'IQD ' : '$'}{record.totalFee.toLocaleString()}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('paidAmount')}>
                                            <Text style={{ color: '#52c41a' }}>{record.currency === 'IQD' ? 'IQD ' : '$'}{record.paidAmount.toLocaleString()}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('remainingAmount')}>
                                            <Text strong style={{ color: '#ff4d4f' }}>{record.currency === 'IQD' ? 'IQD ' : '$'}{record.remainingAmount.toLocaleString()}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('paymentStatus')}>
                                            <Tag color={record.paymentStatus === 'paid' ? 'green' : record.paymentStatus === 'partial' ? 'orange' : 'red'}>
                                                {t(record.paymentStatus)}
                                            </Tag>
                                        </Descriptions.Item>
                                    </Descriptions>
                                ),
                            });
                        }}
                    >
                        {t('view')}
                    </Button>
                    <Button
                        type="primary"
                        icon={<PrinterOutlined />}
                        size="small"
                        style={{
                            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            border: 'none',
                            borderRadius: '6px',
                            boxShadow: '0 2px 8px rgba(250, 112, 154, 0.3)'
                        }}
                        onClick={() => showStudentInvoice(record)}
                    >
                        {t('invoice')}
                    </Button>
                </Space>
            ),
        },
    ];

    // Payment History table (all student payments with discounts)
    const paymentHistoryColumns = [
        {
            title: t('date'),
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
            sorter: (a, b) => dayjs(a.paymentDate).unix() - dayjs(b.paymentDate).unix(),
        },
        {
            title: t('studentId'),
            dataIndex: 'studentId',
            key: 'studentId',
        },
        {
            title: t('studentName'),
            dataIndex: 'studentName',
            key: 'studentName',
        },
        {
            title: t('courseCode'),
            dataIndex: 'courseCode',
            key: 'courseCode',
        },
        {
            title: t('courseName'),
            dataIndex: 'courseName',
            key: 'courseName',
        },
        {
            title: t('currency'),
            dataIndex: 'currency',
            key: 'currency',
            render: (currency) => <Tag color={currency === 'IQD' ? 'blue' : 'green'}>{currency || 'USD'}</Tag>
        },
        {
            title: t('amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) => {
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text>{symbol}{amount.toLocaleString()}</Text>;
            },
            sorter: (a, b) => a.amount - b.amount,
        },
        {
            title: t('discount'),
            dataIndex: 'discount',
            key: 'discount',
            render: (discount, record) => {
                if (!discount || discount === 0) return <Text type="secondary">-</Text>;
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text style={{ color: '#fa8c16' }}>{symbol}{discount.toLocaleString()}</Text>;
            },
            sorter: (a, b) => (a.discount || 0) - (b.discount || 0),
        },
        {
            title: t('netAmount'),
            dataIndex: 'netAmount',
            key: 'netAmount',
            render: (amount, record) => {
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text strong style={{ color: '#52c41a' }}>{symbol}{amount.toLocaleString()}</Text>;
            },
            sorter: (a, b) => a.netAmount - b.netAmount,
        },
        {
            title: t('paymentMethod'),
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            render: (method) => <Tag>{t(method)}</Tag>,
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<FileTextOutlined />}
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                    }}
                    onClick={() => {
                        Modal.info({
                            title: t('paymentDetails'),
                            width: 600,
                            content: (
                                <Descriptions bordered size="small" column={1}>
                                    <Descriptions.Item label={t('date')}>{dayjs(record.paymentDate).format('YYYY-MM-DD')}</Descriptions.Item>
                                    <Descriptions.Item label={t('studentId')}>{record.studentId}</Descriptions.Item>
                                    <Descriptions.Item label={t('studentName')}>{record.studentName}</Descriptions.Item>
                                    <Descriptions.Item label={t('course')}>{record.courseName} ({record.courseCode})</Descriptions.Item>
                                    <Descriptions.Item label={t('currency')}><Tag color={record.currency === 'IQD' ? 'blue' : 'green'}>{record.currency}</Tag></Descriptions.Item>
                                    <Descriptions.Item label={t('amount')}>{record.currency === 'IQD' ? 'IQD ' : '$'}{record.amount.toLocaleString()}</Descriptions.Item>
                                    <Descriptions.Item label={t('discount')}>{record.discount ? `${record.currency === 'IQD' ? 'IQD ' : '$'}${record.discount.toLocaleString()}` : '-'}</Descriptions.Item>
                                    <Descriptions.Item label={t('netAmount')}><Text strong style={{ color: '#52c41a' }}>{record.currency === 'IQD' ? 'IQD ' : '$'}{record.netAmount.toLocaleString()}</Text></Descriptions.Item>
                                    <Descriptions.Item label={t('paymentMethod')}>{t(record.paymentMethod)}</Descriptions.Item>
                                </Descriptions>
                            ),
                        });
                    }}
                >
                    {t('view')}
                </Button>
            ),
        },
    ];

    // Income details table
    const incomeColumns = [
        {
            title: t('date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
            sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
        },
        {
            title: t('receiptNo'),
            dataIndex: 'receiptNumber',
            key: 'receiptNumber'
        },
        {
            title: t('student'),
            key: 'student',
            render: (_, record) => record.student ? `${record.student.firstName} ${record.student.lastName}` : '-'
        },
        {
            title: t('category'),
            dataIndex: 'category',
            key: 'category',
            render: (category) => {
                const colors = { tuition: 'blue', registration: 'green', other: 'default' };
                return <Tag color={colors[category]}>{t(category)}</Tag>;
            }
        },
        {
            title: t('description'),
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: t('currency'),
            dataIndex: 'currency',
            key: 'currency',
            render: (currency) => <Tag color={currency === 'IQD' ? 'blue' : 'green'}>{currency || 'USD'}</Tag>,
        },
        {
            title: t('amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) => {
                const currency = record.currency || 'USD';
                const symbol = currency === 'IQD' ? 'IQD ' : '$';
                return <Text strong style={{ color: '#3f8600' }}>{symbol}{amount?.toLocaleString()}</Text>;
            },
            sorter: (a, b) => a.amount - b.amount,
        },
        {
            title: t('discount'),
            dataIndex: 'discount',
            key: 'discount',
            render: (discount, record) => {
                if (!discount || discount === 0) return <Text type="secondary">-</Text>;
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text style={{ color: '#fa8c16' }}>{symbol}{discount.toLocaleString()}</Text>;
            },
            sorter: (a, b) => (a.discount || 0) - (b.discount || 0),
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="default"
                        size="small"
                        icon={<FileTextOutlined />}
                        style={{
                            borderColor: '#52c41a',
                            color: '#52c41a',
                            borderRadius: '6px'
                        }}
                        onClick={() => {
                            Modal.info({
                                title: t('incomeDetails'),
                                width: 600,
                                content: (
                                    <Descriptions bordered size="small" column={1}>
                                        <Descriptions.Item label={t('date')}>{dayjs(record.date).format('YYYY-MM-DD')}</Descriptions.Item>
                                        <Descriptions.Item label={t('receiptNo')}>{record.receiptNumber}</Descriptions.Item>
                                        <Descriptions.Item label={t('student')}>{record.student ? `${record.student.firstName} ${record.student.lastName}` : '-'}</Descriptions.Item>
                                        <Descriptions.Item label={t('category')}>
                                            <Tag color={record.category === 'course-fee' ? 'blue' : 'green'}>{t(record.category)}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('currency')}>
                                            <Tag color={record.currency === 'IQD' ? 'blue' : 'green'}>{record.currency || 'USD'}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('amount')}>
                                            <Text strong style={{ color: '#3f8600' }}>
                                                {record.currency === 'IQD' ? 'IQD ' : '$'}{record.amount?.toLocaleString()}
                                            </Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('description')}>{record.description}</Descriptions.Item>
                                    </Descriptions>
                                ),
                            });
                        }}
                    >
                        {t('view')}
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        icon={<PrinterOutlined />}
                        style={{
                            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                            border: 'none',
                            borderRadius: '6px',
                            boxShadow: '0 2px 8px rgba(67, 233, 123, 0.3)'
                        }}
                        onClick={() => showInvoice(record, 'income')}
                    >
                        {t('invoice')}
                    </Button>
                </Space>
            ),
        },
    ];

    // Expense details table
    const expenseColumns = [
        {
            title: t('date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
            sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
        },
        {
            title: t('expenseNo'),
            dataIndex: 'expenseNumber',
            key: 'expenseNumber'
        },
        {
            title: t('category'),
            dataIndex: 'category',
            key: 'category',
            render: (category) => {
                const colors = {
                    salary: 'red',
                    rent: 'orange',
                    utilities: 'purple',
                    supplies: 'cyan',
                    other: 'default'
                };
                return <Tag color={colors[category]}>{t(category)}</Tag>;
            }
        },
        {
            title: t('description'),
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: t('paymentMethod'),
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            render: (method) => method ? <Tag>{t(method)}</Tag> : '-',
        },
        {
            title: t('currency'),
            dataIndex: 'currency',
            key: 'currency',
            render: (currency) => <Tag color={currency === 'IQD' ? 'blue' : 'green'}>{currency || 'USD'}</Tag>,
        },
        {
            title: t('amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) => {
                const currency = record.currency || 'USD';
                const symbol = currency === 'IQD' ? 'IQD ' : '$';
                return <Text strong style={{ color: '#cf1322' }}>{symbol}{amount?.toLocaleString()}</Text>;
            },
            sorter: (a, b) => a.amount - b.amount,
        },
        {
            title: t('discount'),
            dataIndex: 'discount',
            key: 'discount',
            render: (discount, record) => {
                if (!discount || discount === 0) return <Text type="secondary">-</Text>;
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text style={{ color: '#fa8c16' }}>{symbol}{discount.toLocaleString()}</Text>;
            },
            sorter: (a, b) => (a.discount || 0) - (b.discount || 0),
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="default"
                        size="small"
                        icon={<FileTextOutlined />}
                        style={{
                            borderColor: '#ff4d4f',
                            color: '#ff4d4f',
                            borderRadius: '6px'
                        }}
                        onClick={() => {
                            Modal.info({
                                title: t('expenseDetails'),
                                width: 600,
                                content: (
                                    <Descriptions bordered size="small" column={1}>
                                        <Descriptions.Item label={t('date')}>{dayjs(record.date).format('YYYY-MM-DD')}</Descriptions.Item>
                                        <Descriptions.Item label={t('category')}>
                                            <Tag color={record.category === 'salary' ? 'red' : record.category === 'rent' ? 'orange' : 'default'}>
                                                {t(record.category)}
                                            </Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('currency')}>
                                            <Tag color={record.currency === 'IQD' ? 'blue' : 'green'}>{record.currency || 'USD'}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('amount')}>
                                            <Text strong style={{ color: '#cf1322' }}>
                                                {record.currency === 'IQD' ? 'IQD ' : '$'}{record.amount?.toLocaleString()}
                                            </Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('paymentMethod')}>{record.paymentMethod ? t(record.paymentMethod) : '-'}</Descriptions.Item>
                                        <Descriptions.Item label={t('description')}>{record.description}</Descriptions.Item>
                                    </Descriptions>
                                ),
                            });
                        }}
                    >
                        {t('view')}
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        icon={<PrinterOutlined />}
                        style={{
                            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            border: 'none',
                            borderRadius: '6px',
                            boxShadow: '0 2px 8px rgba(250, 112, 154, 0.3)'
                        }}
                        onClick={() => showInvoice(record, 'expense')}
                    >
                        {t('invoice')}
                    </Button>
                </Space>
            ),
        },
    ];

    // Category breakdown data by currency
    const incomeCategoryDataIQD = [
        { key: '1', category: t('tuition'), amount: tuitionIncomeIQD, percentage: totalIncomeIQD ? ((tuitionIncomeIQD / totalIncomeIQD) * 100).toFixed(1) : 0, currency: 'IQD' },
        { key: '2', category: t('registration'), amount: registrationIncomeIQD, percentage: totalIncomeIQD ? ((registrationIncomeIQD / totalIncomeIQD) * 100).toFixed(1) : 0, currency: 'IQD' },
        { key: '3', category: t('other'), amount: otherIncomeIQD, percentage: totalIncomeIQD ? ((otherIncomeIQD / totalIncomeIQD) * 100).toFixed(1) : 0, currency: 'IQD' },
    ];

    const incomeCategoryDataUSD = [
        { key: '1', category: t('tuition'), amount: tuitionIncomeUSD, percentage: totalIncomeUSD ? ((tuitionIncomeUSD / totalIncomeUSD) * 100).toFixed(1) : 0, currency: 'USD' },
        { key: '2', category: t('registration'), amount: registrationIncomeUSD, percentage: totalIncomeUSD ? ((registrationIncomeUSD / totalIncomeUSD) * 100).toFixed(1) : 0, currency: 'USD' },
        { key: '3', category: t('other'), amount: otherIncomeUSD, percentage: totalIncomeUSD ? ((otherIncomeUSD / totalIncomeUSD) * 100).toFixed(1) : 0, currency: 'USD' },
    ];

    const expenseCategoryDataIQD = allExpenseCategoriesIQD.map((category, index) => ({
        key: String(index + 1),
        category: category,
        amount: expenseByCategoryIQD[category],
        percentage: totalExpensesIQD ? ((expenseByCategoryIQD[category] / totalExpensesIQD) * 100).toFixed(1) : 0,
        currency: 'IQD'
    }));

    const expenseCategoryDataUSD = allExpenseCategoriesUSD.map((category, index) => ({
        key: String(index + 1),
        category: category,
        amount: expenseByCategoryUSD[category],
        percentage: totalExpensesUSD ? ((expenseByCategoryUSD[category] / totalExpensesUSD) * 100).toFixed(1) : 0,
        currency: 'USD'
    }));

    const breakdownColumns = [
        { title: t('category'), dataIndex: 'category', key: 'category' },
        {
            title: t('amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) => {
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                return <Text strong>{symbol}{amount.toLocaleString()}</Text>;
            },
        },
        {
            title: t('percentage'),
            dataIndex: 'percentage',
            key: 'percentage',
            render: (percentage) => `${percentage}%`,
        },
    ];

    return (
        <div>
            <div className="page-header">
                <Title level={2} style={{ margin: 0 }}>{t('financialReports')}</Title>
                <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    format="YYYY-MM-DD"
                    style={{ width: 'auto' }}
                />
            </div>

            {/* Summary Statistics */}
            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                {/* IQD Income */}
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-green"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(67, 233, 123, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    💰 {t('totalIncome')} (IQD)
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1, wordBreak: 'break-word' }}>
                                    IQD {totalIncomeIQD.toLocaleString()}
                                </div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' }}>
                                    {filteredIncome?.filter(i => i.currency === 'IQD').length || 0} {t('transactions')}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <RiseOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* USD Income */}
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-green"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(67, 233, 123, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    💵 {t('totalIncome')} (USD)
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1, wordBreak: 'break-word' }}>
                                    ${totalIncomeUSD.toLocaleString()}
                                </div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' }}>
                                    {filteredIncome?.filter(i => !i.currency || i.currency === 'USD').length || 0} {t('transactions')}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <RiseOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* Total Income */}
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-purple"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    {t('totalIncome')}
                                </div>
                                <div style={{ fontSize: '24px', fontWeight: 700, color: '#fff', lineHeight: 1.3, wordBreak: 'break-word' }}>
                                    IQD {totalIncomeIQD.toLocaleString()}<br />
                                    ${totalIncomeUSD.toLocaleString()}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <RiseOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* IQD Expenses */}
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-sunset"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(250, 112, 154, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    💰 {t('totalExpenses')} (IQD)
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1, wordBreak: 'break-word' }}>
                                    IQD {totalExpensesIQD.toLocaleString()}
                                </div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' }}>
                                    {filteredExpenses?.filter(e => e.currency === 'IQD').length || 0} {t('transactions')}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <FallOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* USD Expenses */}
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-sunset"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(250, 112, 154, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    💵 {t('totalExpenses')} (USD)
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1, wordBreak: 'break-word' }}>
                                    ${totalExpensesUSD.toLocaleString()}
                                </div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' }}>
                                    {filteredExpenses?.filter(e => !e.currency || e.currency === 'USD').length || 0} {t('transactions')}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <FallOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* Total Expenses */}
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-orange"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(255, 130, 66, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    {t('totalExpenses')}
                                </div>
                                <div style={{ fontSize: '24px', fontWeight: 700, color: '#fff', lineHeight: 1.3, wordBreak: 'break-word' }}>
                                    IQD {totalExpensesIQD.toLocaleString()}<br />
                                    ${totalExpensesUSD.toLocaleString()}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <FallOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* Net Profit IQD */}
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        className={`dashboard-gradient-card ${netProfitIQD >= 0 ? 'gradient-cyan' : 'gradient-sunset'}`}
                        style={{
                            borderRadius: '16px',
                            boxShadow: netProfitIQD >= 0
                                ? '0 8px 24px rgba(79, 172, 254, 0.25)'
                                : '0 8px 24px rgba(250, 112, 154, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    💰 {t('netProfit')} (IQD)
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1, wordBreak: 'break-word' }}>
                                    IQD {netProfitIQD.toLocaleString()}
                                </div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' }}>
                                    {netProfitIQD >= 0 ? t('profit') : t('loss')}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                {netProfitIQD >= 0 ? <RiseOutlined style={{ fontSize: '30px', color: '#fff' }} /> : <FallOutlined style={{ fontSize: '30px', color: '#fff' }} />}
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* Net Profit USD */}
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        className={`dashboard-gradient-card ${netProfitUSD >= 0 ? 'gradient-cyan' : 'gradient-sunset'}`}
                        style={{
                            borderRadius: '16px',
                            boxShadow: netProfitUSD >= 0
                                ? '0 8px 24px rgba(79, 172, 254, 0.25)'
                                : '0 8px 24px rgba(250, 112, 154, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    💵 {t('netProfit')} (USD)
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1, wordBreak: 'break-word' }}>
                                    ${netProfitUSD.toLocaleString()}
                                </div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' }}>
                                    {netProfitUSD >= 0 ? t('profit') : t('loss')}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                {netProfitUSD >= 0 ? <RiseOutlined style={{ fontSize: '30px', color: '#fff' }} /> : <FallOutlined style={{ fontSize: '30px', color: '#fff' }} />}
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* Total Net Profit */}
                <Col xs={24} sm={12} lg={8}>
                    <Card
                        bordered={false}
                        className={`dashboard-gradient-card ${netProfit >= 0 ? 'gradient-purple' : 'gradient-sunset'}`}
                        style={{
                            borderRadius: '16px',
                            boxShadow: netProfit >= 0
                                ? '0 8px 24px rgba(102, 126, 234, 0.25)'
                                : '0 8px 24px rgba(250, 112, 154, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    {t('netProfit')}
                                </div>
                                <div style={{ fontSize: '24px', fontWeight: 700, color: '#fff', lineHeight: 1.3, wordBreak: 'break-word' }}>
                                    IQD {netProfitIQD.toLocaleString()}<br />
                                    ${netProfitUSD.toLocaleString()}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                {netProfit >= 0 ? <RiseOutlined style={{ fontSize: '30px', color: '#fff' }} /> : <FallOutlined style={{ fontSize: '30px', color: '#fff' }} />}
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Additional Stats */}
            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-purple"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)',
                            height: '160px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    {t('activeStudents')}
                                </div>
                                <div style={{ fontSize: '36px', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                                    {activeStudents}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <UserOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-pink"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(240, 147, 251, 0.25)',
                            height: '160px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    {t('activeStaff')}
                                </div>
                                <div style={{ fontSize: '36px', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                                    {activeStaff}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <TeamOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-cyan"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(79, 172, 254, 0.25)',
                            height: '160px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    💵 {t('courseRevenue')} (USD)
                                </div>
                                <div style={{ fontSize: '32px', fontWeight: 700, color: '#fff', lineHeight: 1, wordBreak: 'break-word' }}>
                                    ${courseRevenueUSD.toFixed(2)}
                                </div>
                                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
                                    {t('paid')}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <BookOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-orange"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(255, 159, 64, 0.25)',
                            height: '160px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '8px', fontWeight: 500 }}>
                                    🍊 {t('courseRevenue')} (IQD)
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                                    IQD {courseRevenueIQD.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </div>
                                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginTop: '6px' }}>
                                    {t('paid')}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <BookOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-orange"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(255, 159, 64, 0.25)',
                            height: '160px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    {t('expenses')}
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1, wordBreak: 'break-word' }}>
                                    IQD {totalExpensesIQD.toLocaleString()}
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1, wordBreak: 'break-word', marginTop: '8px' }}>
                                    ${totalExpensesUSD.toLocaleString()}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <WalletOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Collapsible Sections for Details */}
            <Collapse
                defaultActiveKey={['1']}
                style={{ marginBottom: 24 }}
                expandIconPosition="end"
            >
                {/* Financial Summary */}
                <Panel
                    header={
                        <Space>
                            <PieChartOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>{t('financialSummary')}</Title>
                        </Space>
                    }
                    key="1"
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Card title={`${t('incomeBreakdown')} (IQD)`} size="small">
                                <Table
                                    columns={breakdownColumns}
                                    dataSource={incomeCategoryDataIQD}
                                    pagination={false}
                                    size="small"
                                />
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card title={`${t('incomeBreakdown')} (USD)`} size="small">
                                <Table
                                    columns={breakdownColumns}
                                    dataSource={incomeCategoryDataUSD}
                                    pagination={false}
                                    size="small"
                                />
                            </Card>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col xs={24} md={12}>
                            <Card title={`${t('expenseBreakdown')} (IQD)`} size="small">
                                <Table
                                    columns={breakdownColumns}
                                    dataSource={expenseCategoryDataIQD}
                                    pagination={false}
                                    size="small"
                                />
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card title={`${t('expenseBreakdown')} (USD)`} size="small">
                                <Table
                                    columns={breakdownColumns}
                                    dataSource={expenseCategoryDataUSD}
                                    pagination={false}
                                    size="small"
                                />
                            </Card>
                        </Col>
                    </Row>
                </Panel>

                {/* Course Statistics */}
                <Panel
                    header={
                        <Space>
                            <BookOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                            <Title level={4} style={{ margin: 0 }}>{t('courseEnrollmentStats')}</Title>
                        </Space>
                    }
                    key="2"
                >
                    <Table
                        columns={courseStatsColumns}
                        dataSource={courseStatsData}
                        rowKey="courseCode"
                        pagination={{ pageSize: 5, simple: true }}
                        loading={studentsLoading || coursesLoading}
                        size="small"
                    />
                </Panel>

                {/* Unpaid Students */}
                <Panel
                    header={
                        <Space>
                            <WalletOutlined style={{ fontSize: '18px', color: '#ff4d4f' }} />
                            <Title level={4} style={{ margin: 0 }}>{t('unpaidStudents')}</Title>
                            <Tag color="red">{unpaidStudents.length}</Tag>
                        </Space>
                    }
                    key="3"
                >
                    <Table
                        columns={unpaidStudentsColumns}
                        dataSource={unpaidStudents}
                        rowKey="key"
                        pagination={{ pageSize: 5, showSizeChanger: true, showTotal: (total) => `${t('total')} ${total}` }}
                        loading={studentsLoading}
                        scroll={{ x: 1000 }}
                        size="small"
                        summary={(pageData) => {
                            const totalPendingIQD = pageData.filter(p => p.currency === 'IQD').reduce((sum, item) => sum + (item.remainingAmount || 0), 0);
                            const totalPaidIQD = pageData.filter(p => p.currency === 'IQD').reduce((sum, item) => sum + (item.paidAmount || 0), 0);
                            const totalFeesIQD = pageData.filter(p => p.currency === 'IQD').reduce((sum, item) => sum + (item.totalFee || 0), 0);
                            const totalPendingUSD = pageData.filter(p => p.currency !== 'IQD').reduce((sum, item) => sum + (item.remainingAmount || 0), 0);
                            const totalPaidUSD = pageData.filter(p => p.currency !== 'IQD').reduce((sum, item) => sum + (item.paidAmount || 0), 0);
                            const totalFeesUSD = pageData.filter(p => p.currency !== 'IQD').reduce((sum, item) => sum + (item.totalFee || 0), 0);
                            return (
                                <>
                                    <Table.Summary fixed>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={4}>
                                                <Text strong>{t('pageTotal')} (IQD)</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}>
                                                <Text strong>IQD {totalFeesIQD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}>
                                                <Text strong style={{ color: '#52c41a' }}>IQD {totalPaidIQD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}>
                                                <Text strong style={{ color: '#ff4d4f' }}>IQD {totalPendingIQD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4} colSpan={2} />
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={4}>
                                                <Text strong>{t('pageTotal')} (USD)</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}>
                                                <Text strong>${totalFeesUSD.toFixed(2)}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}>
                                                <Text strong style={{ color: '#52c41a' }}>${totalPaidUSD.toFixed(2)}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}>
                                                <Text strong style={{ color: '#ff4d4f' }}>${totalPendingUSD.toFixed(2)}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4} colSpan={2} />
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                </>
                            );
                        }}
                    />
                </Panel>

                {/* Payment History */}
                <Panel
                    header={
                        <Space>
                            <DollarOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>{t('paymentHistory')}</Title>
                            <Tag color="blue">{filteredPaymentHistory.length}</Tag>
                        </Space>
                    }
                    key="6"
                >
                    <Table
                        columns={paymentHistoryColumns}
                        dataSource={filteredPaymentHistory}
                        rowKey="key"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `${t('total')} ${total}`
                        }}
                        loading={studentsLoading}
                        scroll={{ x: 1200 }}
                        size="small"
                        summary={(pageData) => {
                            const totalAmountIQD = pageData.filter(p => p.currency === 'IQD').reduce((sum, item) => sum + (item.amount || 0), 0);
                            const totalAmountUSD = pageData.filter(p => p.currency === 'USD').reduce((sum, item) => sum + (item.amount || 0), 0);
                            const totalDiscountIQD = pageData.filter(p => p.currency === 'IQD').reduce((sum, item) => sum + (item.discount || 0), 0);
                            const totalDiscountUSD = pageData.filter(p => p.currency === 'USD').reduce((sum, item) => sum + (item.discount || 0), 0);
                            const totalNetIQD = pageData.filter(p => p.currency === 'IQD').reduce((sum, item) => sum + (item.netAmount || 0), 0);
                            const totalNetUSD = pageData.filter(p => p.currency === 'USD').reduce((sum, item) => sum + (item.netAmount || 0), 0);

                            return (
                                <>
                                    <Table.Summary fixed>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={5}>
                                                <Text strong>{t('pageTotal')} (IQD)</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}>
                                                <Tag color="blue">IQD</Tag>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}>
                                                <Text strong>IQD {totalAmountIQD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}>
                                                <Text strong style={{ color: '#fa8c16' }}>IQD {totalDiscountIQD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}>
                                                <Text strong style={{ color: '#52c41a' }}>IQD {totalNetIQD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} />
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={5}>
                                                <Text strong>{t('pageTotal')} (USD)</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}>
                                                <Tag color="green">USD</Tag>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2}>
                                                <Text strong>${totalAmountUSD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3}>
                                                <Text strong style={{ color: '#fa8c16' }}>${totalDiscountUSD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4}>
                                                <Text strong style={{ color: '#52c41a' }}>${totalNetUSD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} />
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                </>
                            );
                        }}
                    />
                </Panel>

                {/* Income Details */}
                <Panel
                    header={
                        <Space>
                            <RiseOutlined style={{ fontSize: '18px', color: '#3f8600' }} />
                            <Title level={4} style={{ margin: 0 }}>{t('incomeDetails')}</Title>
                            <Tag color="green">{filteredIncome?.length || 0} {t('transactions')}</Tag>
                        </Space>
                    }
                    key="4"
                >
                    <Table
                        columns={incomeColumns}
                        dataSource={filteredIncome}
                        rowKey="_id"
                        loading={incomeLoading}
                        pagination={{ pageSize: 5, showSizeChanger: true, showTotal: (total) => `${t('total')} ${total}` }}
                        scroll={{ x: 1000 }}
                        size="small"
                        summary={(pageData) => {
                            const pageTotalIQD = pageData.filter(p => p.currency === 'IQD').reduce((sum, item) => sum + (item.amount || 0), 0);
                            const pageTotalUSD = pageData.filter(p => p.currency !== 'IQD').reduce((sum, item) => sum + (item.amount || 0), 0);
                            return (
                                <>
                                    <Table.Summary fixed>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={5}>
                                                <Text strong>{t('pageTotal')} (IQD)</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}>
                                                <Text strong style={{ color: '#3f8600' }}>IQD {pageTotalIQD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={5}>
                                                <Text strong>{t('pageTotal')} (USD)</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}>
                                                <Text strong style={{ color: '#3f8600' }}>${pageTotalUSD.toFixed(2)}</Text>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                </>
                            );
                        }}
                    />
                </Panel>

                {/* Expense Details */}
                <Panel
                    header={
                        <Space>
                            <FallOutlined style={{ fontSize: '18px', color: '#cf1322' }} />
                            <Title level={4} style={{ margin: 0 }}>{t('expenseDetails')}</Title>
                            <Tag color="red">{filteredExpenses?.length || 0} {t('transactions')}</Tag>
                        </Space>
                    }
                    key="5"
                >
                    <Table
                        columns={expenseColumns}
                        dataSource={filteredExpenses}
                        rowKey="_id"
                        loading={expensesLoading}
                        pagination={{ pageSize: 5, showSizeChanger: true, showTotal: (total) => `${t('total')} ${total}` }}
                        scroll={{ x: 1000 }}
                        size="small"
                        summary={(pageData) => {
                            const pageTotalIQD = pageData.filter(p => p.currency === 'IQD').reduce((sum, item) => sum + (item.amount || 0), 0);
                            const pageTotalUSD = pageData.filter(p => p.currency !== 'IQD').reduce((sum, item) => sum + (item.amount || 0), 0);
                            return (
                                <>
                                    <Table.Summary fixed>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={4}>
                                                <Text strong>{t('pageTotal')} (IQD)</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}>
                                                <Text strong style={{ color: '#cf1322' }}>IQD {pageTotalIQD.toLocaleString()}</Text>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={4}>
                                                <Text strong>{t('pageTotal')} (USD)</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1}>
                                                <Text strong style={{ color: '#cf1322' }}>${pageTotalUSD.toFixed(2)}</Text>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                </>
                            );
                        }}
                    />
                </Panel>
            </Collapse>

            {/* Invoice Modal */}
            <Modal
                title={null}
                open={invoiceVisible}
                onCancel={() => setInvoiceVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setInvoiceVisible(false)}>
                        {t('close')}
                    </Button>,
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
                        {t('print')}
                    </Button>,
                ]}
                width={800}
            >
                <div ref={invoiceRef} style={{ padding: '40px' }}>
                    {/* Invoice Header */}
                    <div style={{ textAlign: 'center', marginBottom: '30px', borderBottom: '3px solid #1890ff', paddingBottom: '20px' }}>
                        {user?.academy?.logo && (
                            <div style={{ marginBottom: '10px' }}>
                                <img
                                    src={`${user.academy.logo}`}
                                    alt="Academy Logo"
                                    style={{ maxHeight: '60px', maxWidth: '200px', objectFit: 'contain' }}
                                />
                            </div>
                        )}
                        <Title level={2} style={{ marginBottom: '5px', color: '#1890ff' }}>{user?.academy?.name || 'Paymanga'}</Title>
                        <Text style={{ fontSize: '16px' }}>{t('institute')}</Text>
                        <div style={{ marginTop: '10px' }}>
                            <Text type="secondary">{t('languageInstituteManagementSystem')}</Text>
                        </div>
                    </div>

                    {/* Invoice Type */}
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                        <Tag color={invoiceType === 'income' ? 'green' : invoiceType === 'unpaid' ? 'orange' : 'red'} style={{ fontSize: '18px', padding: '8px 20px' }}>
                            {invoiceType === 'income' ? t('incomeReceipt') : invoiceType === 'unpaid' ? t('paymentInvoice') : t('expenseReceipt')}
                        </Tag>
                    </div>

                    {/* Invoice Details */}
                    {selectedInvoice && (
                        <>
                            <Descriptions bordered column={2} size="small">
                                <Descriptions.Item label={t('date')}>
                                    {dayjs(selectedInvoice.date).format('YYYY-MM-DD')}
                                </Descriptions.Item>
                                {selectedInvoice.receiptNumber && (
                                    <Descriptions.Item label={t('receiptNo')}>
                                        {selectedInvoice.receiptNumber}
                                    </Descriptions.Item>
                                )}
                                {selectedInvoice.student && (
                                    <Descriptions.Item label={t('student')} span={2}>
                                        {selectedInvoice.student.firstName} {selectedInvoice.student.lastName}
                                    </Descriptions.Item>
                                )}
                                {invoiceType === 'unpaid' && selectedInvoice.course && (
                                    <Descriptions.Item label={t('course')} span={2}>
                                        {selectedInvoice.course}
                                    </Descriptions.Item>
                                )}
                                {invoiceType === 'unpaid' && selectedInvoice.totalFee && (
                                    <>
                                        <Descriptions.Item label={t('totalFee')}>
                                            ${selectedInvoice.totalFee?.toFixed(2)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('paidAmount')}>
                                            <Text style={{ color: '#52c41a' }}>${selectedInvoice.paidAmount?.toFixed(2)}</Text>
                                        </Descriptions.Item>
                                    </>
                                )}
                                <Descriptions.Item label={t('category')}>
                                    {t(selectedInvoice.category)}
                                </Descriptions.Item>
                                {selectedInvoice.paymentMethod && selectedInvoice.paymentMethod !== 'pending' && (
                                    <Descriptions.Item label={t('paymentMethod')}>
                                        {t(selectedInvoice.paymentMethod)}
                                    </Descriptions.Item>
                                )}
                                {selectedInvoice.description && (
                                    <Descriptions.Item label={t('description')} span={2}>
                                        {selectedInvoice.description}
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label={invoiceType === 'unpaid' ? t('remainingAmount') : t('amount')} span={2}>
                                    <Text strong style={{ fontSize: '20px', color: invoiceType === 'expense' ? '#cf1322' : invoiceType === 'unpaid' ? '#ff4d4f' : '#3f8600' }}>
                                        ${selectedInvoice.amount?.toFixed(2)}
                                    </Text>
                                </Descriptions.Item>
                            </Descriptions>

                            {/* Footer */}
                            <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #d9d9d9' }}>
                                <Row>
                                    <Col span={12}>
                                        <div>
                                            <Text type="secondary">{t('generatedOn')}: {dayjs().format('YYYY-MM-DD HH:mm')}</Text>
                                        </div>
                                    </Col>
                                    <Col span={12} style={{ textAlign: 'right' }}>
                                        <div>
                                            <Text type="secondary">{t('authorizedSignature')}</Text>
                                            <div style={{ marginTop: '30px', borderBottom: '1px solid #000', width: '200px', marginLeft: 'auto' }}></div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default Reports;
